import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { timer } from 'rxjs/observable/timer';

import { ServerResponseConverter } from '../converters';
import { SocialCodeEnum, SocialMethodEnum, SocialNetworks } from '../dictionaries';
import {
  Configuration,
  MessageAccount,
  Profile,
  RequestOptions,
  ServerResponse,
  UserCredentials,
  UserRequest,
  EmailConfirm
} from '../models';
import { UserPayment } from '../models/user-payment';
import { AppConfig } from './app.config';
import { CookieService } from './cookie.service';
import { RestService } from './rest.service';
import { Subject } from 'rxjs/Subject';
import {Router} from '@angular/router';

@Injectable()
export class AccountService {

  constructor(
    private restService: RestService,
    private cookieService: CookieService,
    private _router: Router
  ) {
    this.Config = AppConfig.config;
    this.vkAppKey = this.Config.AppKeys.vkAppKey;
    this.fbAppKey = this.Config.AppKeys.fbAppKey;
    this.okAppKey = this.Config.AppKeys.okAppKey;
    this.ggSecretKey = this.Config.AppKeys.googleSecretKey;
    this.authUrl = this.Config.AuthRedirectUrl;

    window['angularComponent'] = {
      authWindowCallback: this.authWindowCallback,
      authCodeSubject: this.authCodeSubject
    };

    this.restService.getSessionExceptionSub().subscribe(exception => {
      if (exception) {
        this.logOut();
      }
    });
  }

  public returnUrl: string;
  public emailConfirmed = new BehaviorSubject(false);
  public showNotifyPopup = new BehaviorSubject(false);
  public showStudyPopup = new BehaviorSubject(false);

  private Config: Configuration;
  private userCredentials: UserCredentials;
  private defaultUser: UserCredentials;
  private authCodeSubject = new BehaviorSubject<string>(null);
  private totalMessagesSubject = new BehaviorSubject<any[]>(null);
  private responseConverter = new ServerResponseConverter<null>();
  private newMessageSubject = new BehaviorSubject<boolean>(false);
  private startTimerSignUpDialog = new BehaviorSubject<boolean>(false);
  private blockGetCredentialsPromise = false;

  private vkAppKey;
  private fbAppKey;
  private okAppKey;
  private ggSecretKey;
  private authUrl;

  private logOutSubject = new Subject<number>();
  private semaphorPromise: Promise<UserCredentials>;

  public register(user: UserRequest): Promise<ServerResponse<string>> {
    return new Promise<ServerResponse<string>>(resolve => {
      user.ExternalSystem = user.ExternalSystem || SocialNetworks.None;
      user.Name = user.Email;
      user.Region = user.Region || 'null';
      user.Sex = user.Sex || 1;
      user.Id_Tarif = '1';
      user.FavPaymentType = 1;
      user.Hid_ExternalUser = user.Hid_ExternalUser || '0';

      this.restService.registerUser(user).then(
        registrationResult => {
          if (registrationResult !== null) {
            resolve(registrationResult);
          }
        });
    });
  }

  public logIn(user: UserRequest): Promise<ServerResponse<string>> {
    return new Promise<ServerResponse<string>>(resolve => {
      const loginUser = new UserRequest();
      loginUser.Name = user.Email;
      loginUser.HPassword = user.HPassword;
      loginUser.ExternalSystem = '0';
      loginUser.Hid_ExternalUser = '0';
      loginUser.signature = AppConfig.config.HashSalt;
      loginUser.Id_app = this.Config.AppId;
      loginUser.ApiVersion = 'v1';

      this.restService.logInUser(loginUser).then(response => {
        if (response !== null) {
          const cred = this.responseConverter.GetUser(response);

          if (response.Result.ReturnCode < 0) {
            resolve(response);
            return;
          }

          this.completeUserCredentials(cred).then(userCredentials => {
            if (userCredentials) {
              this.userCredentials = userCredentials;
              resolve(response);
            }
          });
        }
      });
    });
  }

  public setUserCredentials(userData): Promise<UserCredentials> {
    const credentials = this.responseConverter.GetUser(userData);
    this.blockGetCredentialsPromise = true;
    return this.completeUserCredentials(credentials, true);
  }

  public logOutWithRequest(): Promise<any> {
    return new Promise<any>(resolve => {
      this.getCredentialsPromise().then(cred => {
        if (!cred) {
          resolve();
          return;
        }
        const logoutRequest = new UserRequest();
        logoutRequest.Controller0 = 'Session';
        logoutRequest.Method = 'Logout';
        logoutRequest.Id_session = cred.Id_Session;
        logoutRequest.Id_user = cred.Id_User;
        logoutRequest.signature = AppConfig.config.HashSalt;
        logoutRequest.Id_app = this.Config.AppId;
        logoutRequest.ApiVersion = 'v1';

        this.restService.post(logoutRequest).then(() => {
          this.logOut();
          resolve();
        });
      });
    });
  }

  public logOut() {
    this.cookieService.clearCookies().then(() => {
      this.userCredentials = null;
      const justValueForNext = 1;
      this.logOutSubject.next(justValueForNext);
      window.location.href = '';
    });
  }

  public getLogOutSubscribtion() {
    return this.logOutSubject.asObservable();
  }
  // forceResult используется для мест, где нужно вернуть креденшелы, даже если их нет
  public getCredentialsPromise(forceUpdate: boolean = false, forceResult: boolean = false): Promise<UserCredentials> {
    return new Promise<UserCredentials>((resolve) => {
      if (this.userCredentials && !forceUpdate) {
        resolve(this.userCredentials);
        return;
      }
      let userFromCookies = this.cookieService.getCookies(forceUpdate);
      if (!userFromCookies) {
        userFromCookies = {
          Id_Session: this.Config.DefaultSessionId,
          Id_User: this.Config.DefaultUserId,
          Id_Tarif: 0,
          EMail: '',
          Region: null,
          Sex: null,
          TarifName: null,
          PaymentActiveDue: null,
          Vallet: null,
          ExternalSystem: null,
          SsoAuth: null
        };
      }

      if (!this.blockGetCredentialsPromise) {
        this.completeUserCredentials(userFromCookies).then(cred => {
          if (cred || forceResult) {
            this.userCredentials = cred;
            resolve(this.userCredentials);
          }
        });
      }
    });
  }

  private completeUserCredentials(cred: UserCredentials, allowElse: boolean = false): Promise<UserCredentials> {
    if (this.semaphorPromise) {
      if (allowElse) {
        this.semaphorPromise = this.makeNewSemaphorPromise(cred);
        return this.semaphorPromise;
      }
      this.semaphorPromise.then(result => {
        if (result && result.Id_Session === cred.Id_Session) {
          return this.semaphorPromise;
        } else {
          this.semaphorPromise = this.makeNewSemaphorPromise(cred);
        }
      });
    } else {
      this.semaphorPromise = this.makeNewSemaphorPromise(cred);
    }
    return this.semaphorPromise;
  }

  public makeNewSemaphorPromise(cred) {
    return new Promise<UserCredentials>(resolve => {
      const userRequest = new UserRequest();
      userRequest.Id_session = cred.Id_Session;
      userRequest.Id_app = this.Config.AppId;
      userRequest.Page = 1;
      userRequest.PageSize = 1000;
      userRequest.Controller0 = 'User';
      userRequest.EntityId0 = cred.Id_User;
      userRequest.Method = 'Print';
      userRequest.ApiVersion = 'v1';

      if (userRequest.EntityId0 === null || userRequest.EntityId0 === undefined) {
        resolve();
        return;
      }
      this.restService.checkUserStatus(cred).then(stat => {
        if (!stat) {
          this.semaphorPromise = null;
          this.logOut();
          resolve();
          return;
        }

        const statParse = JSON.parse(JSON.stringify(stat));
        userRequest.Id_session = statParse.Result.Id_Session;

        this.restService.get(userRequest).then(x => {
          const response = x;

          if (response === null || !response.Result.Resultset[0]) {
            this.semaphorPromise = null;
            resolve();
            return;
          }

          cred.TarifName = response.Result.Resultset[0].TarifName;
          cred.Id_Tarif = response.Result.Resultset[0].Id_Tarif;
          cred.ExternalSystem = response.Result.Resultset[0].ExternalSystem;
          cred.EMail = response.Result.Resultset[0].EMail;
          cred.PaymentActiveDue = response.Result.Resultset[0].PaymentActiveDue;
          cred.Vallet = response.Result.Resultset[0].Vallet;
          cred.Id_Session = statParse.Result.Id_Session;
          cred.Region = response.Result.Resultset[0].Region;
          cred.SsoAuth = statParse.Result.SsoAuth;

          let lifeTime: Date;
          if (cred.PaymentActiveDue) {
            let rawDate = cred.PaymentActiveDue;
            const dateAtoms = rawDate.split('.');
            rawDate = [dateAtoms[1], dateAtoms[0], dateAtoms[2]].join('.');
            lifeTime = new Date();
            lifeTime.setTime(Date.parse(rawDate));
          }
          let dTime = lifeTime.getTime() - new Date().getTime();

          const options = {
            path: '/', expires: (dTime > 0)
              ? (dTime > this.Config.CookiesLifetime ? this.Config.CookiesLifetime : lifeTime.toUTCString())
              : this.Config.CookiesLifetime
          };
          const idSession =
            cred.Id_Session !== String(statParse.Result.Id_Session) ?
              String(statParse.Result.Id_Session) : cred.Id_Session;

          this.cookieService.writeCookies(cred, idSession,  options);
          if (this.blockGetCredentialsPromise) this.blockGetCredentialsPromise = false;

          if (cred.Id_Tarif === 1 && cred.Id_Session !== '-111') {
            this.showNotifyPopup.next(true);
            if (this.cookieService.getCookie('isNotifyPop') === undefined) {
              this.cookieService.writeNotifyPopCookie(true, this.Config.FirstNotifyCookiesLifetime);
            }
          }

          if (cred.Id_Session !== '-111') {
            this.showStudyPopup.next(true);
            if (this.cookieService.getCookie('isStudyPop') === undefined) {
              this.cookieService.writeStudyPopCookie(true, this.Config.studyCookiesLifeTime);
            }
          }

          dTime = new Date(options.expires.toString()).getTime() - new Date().getTime();
          timer(dTime, null, null).subscribe(e => {
            this.getCredentialsPromise(true);
          });

          this.semaphorPromise = null;
          resolve(cred);
        });
      });
    });
  }

  public showNotifyPopupSub() {
    return this.showNotifyPopup.asObservable();
  }

  public showStudyPopupSub() {
    return this.showStudyPopup.asObservable();
  }

  public showSignUpDialog() {
    return this.startTimerSignUpDialog;
  }

  public getUserProfiles() {
    return new Promise<Profile[]>(resolve => {
      this.getCredentialsPromise(true).then(() => {
        const userRequest = new UserRequest();
        userRequest.Id_session = this.userCredentials.Id_Session;
        userRequest.Id_app = this.Config.AppId;
        userRequest.Page = 1;
        userRequest.PageSize = 1000;
        userRequest.Controller0 = 'User';
        userRequest.EntityId0 = this.userCredentials.Id_User;
        userRequest.Section = 'Profile';
        userRequest.Method = 'Print';
        userRequest.ApiVersion = 'v1';
        if (userRequest.EntityId0 !== null && userRequest.EntityId0 !== undefined) {
          this.restService.get(userRequest).then(x => {
            if (x !== null) {
              const response: ServerResponse<Profile> = x;
              resolve(response.Result.Resultset);
            }
          });
        }
      });
    });
  }

  logInVk(): Promise<string> {
    return new Promise(resolve => {
      const scope = '&scope=email';
      const response = '&response_type=code';
      const goTo =
        // tslint:disable-next-line:max-line-length
        `https://oauth.vk.com/authorize?client_id=${this.vkAppKey}${scope}&display=page&redirect_uri=${this.authUrl}${response}&v=5.73&state=VK`;
      const url = `/auth?goto=${encodeURIComponent(goTo)}`;
      const popup =
        window.open(url,
          'example', 'scrollbars=1,location=0,status=0,fullscreen=no,width=1000,height=500,top='
          + (screen.height - 500) / 2 + ',left=' + (screen.width - 1e3) / 2);
      const timerSubscription = timer(0, 100).subscribe(() => {
        if (popup && popup.closed) {
          resolve('closed');
          timerSubscription.unsubscribe();
        }
      });
    });
  }

  getVkSession(code: string) {
    return new Promise<UserCredentials>(resolve => {
      this.restService.getSocialUserInfoPromise(code, SocialMethodEnum.VK, SocialCodeEnum.VK).then(userData => {
        this.setUserCredentials(userData).then(cred => {
          this.userCredentials = cred;
          resolve(cred);
        });
      });
    });
  }

  logInFb() {
    return new Promise(resolve => {
      const scope = '&scope=email';
      // tslint:disable-next-line:max-line-length
      const goTo = `https://www.facebook.com/v2.12/dialog/oauth?client_id=${this.fbAppKey}${scope}&auth_type=rerequest&redirect_uri=${this.authUrl}&state=FB`;
      const url = `/auth?goto=${encodeURIComponent(goTo)}`;
      const popup = window.open(url, 'example', 'scrollbars=1,location=0,status=0,fullscreen=no,width=1000,height=500,top='
        + (screen.height - 500) / 2 + ',left=' + (screen.width - 1e3) / 2);

      const timerSubscription = timer(0, 100).subscribe(() => {
        if (popup && popup.closed) {
          resolve('closed');
          timerSubscription.unsubscribe();
        }
      });
    });
  }

  getFbSession(code: string): Promise<UserCredentials> {
    return new Promise<UserCredentials>(resolve => {
      this.restService.getSocialUserInfoPromise(code, SocialMethodEnum.Facebook, SocialCodeEnum.Facebook).then(userData => {
        this.setUserCredentials(userData).then(cred => {
          this.userCredentials = cred;
          resolve(cred);
        });
      });
    });
  }

  confirm(key: string): Promise<void> {
    return this.restService.confirmEmail(key).then( (x: any) => {
      if (x.Result.Id_Session) {
        this.setUserCredentials(x).then(cred => {
          this.userCredentials = cred;
        });
      }
      this.emailConfirmed.next(true);
    });
  }

  public getConfirm() {
    return this.emailConfirmed.asObservable();
  }

  logInOk() {
    return new Promise(resolve => {
      const scope = `&scope=VALUABLE_ACCESS;GET_EMAIL;LONG_ACCESS_TOKEN`;
      const response = '&response_type=code';
      const goTo =
        // tslint:disable-next-line:max-line-length
        `https://connect.ok.ru/oauth/authorize?client_id=${this.okAppKey}${scope}${response}&redirect_uri=${this.authUrl}&layout=w&state=OK`;
      const url = `/auth?goto=${encodeURIComponent(goTo)}`;

      const popup = window.open(url, 'example', 'scrollbars=1,location=0,status=0,fullscreen=no,width=1000,height=500,top='
        + (screen.height - 500) / 2 + ',left=' + (screen.width - 1e3) / 2);

      const timerSubscription = timer(0, 100).subscribe(() => {
        if (popup && popup.closed) {
          resolve('closed');
          timerSubscription.unsubscribe();
        }
      });
    });
  }

  getOkSession(code: string): Promise<UserCredentials> {
    return new Promise<UserCredentials>(resolve => {
      this.restService.getSocialUserInfoPromise(code, SocialMethodEnum.OK, SocialCodeEnum.OK).then(userData => {
        this.setUserCredentials(userData).then(cred => {
          this.userCredentials = cred;
          resolve(cred);
        });
      });
    });
  }

  logInGoogle() {
    return new Promise(resolve => {
      const scope = '&scope=https://www.googleapis.com/auth/userinfo.email';
      const response = '&response_type=token';
      const goTo =
        // tslint:disable-next-line:max-line-length
        `https://accounts.google.com/o/oauth2/v2/auth?client_id=${this.ggSecretKey}${response}&redirect_uri=${this.authUrl}${scope}&state=GG`;
      const url = `/auth?goto=${encodeURIComponent(goTo)}`;

      const popup = window.open(url, 'example', 'scrollbars=1,location=0,status=0,fullscreen=no,width=1000,height=500,top='
        + (screen.height - 500) / 2 + ',left=' + (screen.width - 1e3) / 2);

      const timerSubscription = timer(0, 100).subscribe(() => {
        if (popup && popup.closed) {
          resolve('closed');
          timerSubscription.unsubscribe();
        }
      });
    });
  }

  getGoogleSession(code: string): Promise<UserCredentials> {
    return new Promise<UserCredentials>(resolve => {
      this.restService.getSocialUserInfoPromise(code, SocialMethodEnum.Google, SocialCodeEnum.Google).then(userData => {
        this.setUserCredentials(userData).then(cred => {
          this.userCredentials = cred;
          resolve(cred);
        });
      });
    });
  }

  authWindowCallback() {
    window['angularComponent'].authCodeSubject.next('done');
  }

  authWindowClosed(): Observable<string> {
    return this.authCodeSubject.asObservable();
  }

  passwordRestore(name: string): Promise<ServerResponse<any>> {
    return new Promise<ServerResponse<any>>(resolve => {
      const userRequest = new UserRequest();
      userRequest.Controller0 = 'User';
      userRequest.Method = 'RestorePassword';
      userRequest.ApiVersion = 'v1';
      userRequest.Name = name;
      userRequest.signature = this.Config.HashSalt;
      userRequest.Id_app = this.Config.AppId;

      this.restService.post(userRequest).then(resp => {
        resolve(resp);
      });
    });
  }

  deleteAccount(): Promise<ServerResponse<any>> {
    const userRequest = new UserRequest();
    userRequest.Id_session = this.userCredentials.Id_Session;
    userRequest.Id_app = this.Config.AppId;
    userRequest.Controller0 = 'User';
    userRequest.EntityId0 = this.userCredentials.Id_User;
    userRequest.Method = 'Delete';
    userRequest.ApiVersion = 'v1';

    return this.restService.post(userRequest);
  }

  public getPersonalMessages(): Observable<any> {
    const request = new RequestOptions();
    request.Id_session = this.userCredentials.Id_Session;
    request.Id_app = this.Config.AppId;
    request.ApiVersion = 'v1';
    request.ManualJSON = 0;
    request.Controller0 = 'User';
    request.EntityId0 = this.userCredentials.Id_User;
    request.Controller1 = 'Message';
    request.Method = 'Print';

    this.restService.get(request).then(message => {
      if (message !== null) {
        const response: ServerResponse<any> = message;
        if (response.Result) {
          this.totalMessagesSubject.next(response.Result.Resultset);
        }
      }
    });

    return this.totalMessagesSubject.asObservable();
  }

  public deleteMessage(id_message: number): Promise<any> {
    return new Promise<any>(resolve => {
      const request = new RequestOptions();
      request.Id_session = this.userCredentials.Id_Session;
      request.Id_app = this.Config.AppId;
      request.ApiVersion = 'v1';
      request.ManualJSON = 0;
      request.Controller0 = 'User';
      request.EntityId0 = this.userCredentials.Id_User;
      request.Controller1 = 'Message';
      request.EntityId1 = id_message;
      request.Method = 'delete';

      this.restService.post(request).then(delMessageResult => {
        if (delMessageResult !== null) {
          const response: ServerResponse<any> = delMessageResult;
          if (response.Result) {
            resolve(response.Result);
          }
          this.getNewMessageInfo().subscribe(newMessage => {
            resolve(response);
          });
        }
      });
    });
  }

  public changePassword(oldPassword: string, newPassword: string) {
    return new Promise<ServerResponse<any>>(res => {
      const request = new RequestOptions();
      request.Id_session = this.userCredentials.Id_Session;
      request.Id_app = this.Config.AppId;
      request.ApiVersion = 'v1';
      request.ManualJSON = 0;
      request.Controller0 = 'User';
      request.EntityId0 = this.userCredentials.Id_User;
      request.Method = 'Update';
      request.HOldPassword = oldPassword;
      request.HNewPassword = newPassword;

      this.restService.post(request).then(x => {
        if (x !== null) {
          const response: ServerResponse<any> = x;
          res(response);
        }
      });
    });
  }

  public changeEmail(address: string, password: string) {
    return new Promise<ServerResponse<any>>(res => {
      this.restService.changeEmail(this.userCredentials.Id_User, address, password).then(resp => {
        res(resp);
      });
    });
  }

  public changeRegion(region: string) {
    return new Promise<ServerResponse<any>>(res => {
      const id_user = this.userCredentials.Id_User;
      const id_session = this.userCredentials.Id_Session;
      this.restService.changeRegion(id_user, id_session, region).then(resp => {
        res(resp);
      });
    });
  }

  public confirmEmailChange(key: string, session: string) {
    return new Promise<any>(resolve => {
      this.restService.confirmEmailChange(key, this.userCredentials.Id_Session || session).then((x: ServerResponse<any>) => {
        resolve(x);
      });
    });
  }

  public getPayments(): Promise<UserPayment[]> {
    return new Promise<UserPayment[]>(resolve => {
      let userPaymentsSubject: UserPayment[] = [];

      const request = new RequestOptions();
      request.Id_session = this.userCredentials.Id_Session;
      request.Id_app = this.Config.AppId;
      request.ApiVersion = 'v1';
      request.ManualJSON = 0;
      request.Controller0 = 'User';
      request.EntityId0 = this.userCredentials.Id_User;
      request.Section = 'Payments';
      request.Method = 'Print';

      this.restService.get(request).then(response => {
        if (response.Result) {
          response.Result.Resultset.forEach(payment => {
            let dateString = payment.D_Payment;
            const dateTimeAtoms = dateString.split(' ');
            const dateAtoms = dateTimeAtoms[0].split('.');
            dateString = [dateAtoms.reverse().join('-'), dateTimeAtoms[1]].join(', ');

            const paymentDate = new Date(dateString);
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            payment.D_Payment = paymentDate.toLocaleString('ru-RU', options);
          });
          userPaymentsSubject = response.Result.Resultset;
        }

        resolve(userPaymentsSubject);
      });
    });
  }

  public getNewMessageInfo() {
    const request = new RequestOptions();
    request.Id_session = this.userCredentials.Id_Session;
    request.Id_app = this.Config.AppId;
    request.ApiVersion = 'v1';
    request.ManualJSON = 0;
    request.Controller0 = 'User';
    request.EntityId0 = this.userCredentials.Id_User;
    request.Section = 'Message';
    request.Method = 'Print';

    this.restService.get(request).then(message => {
      if (message !== null) {
        const response: ServerResponse<MessageAccount> = message;

        const newMessageCode = 1;
        const isNew = response.Result.Resultset.findIndex(messageInArray => messageInArray.IsNewMessage === newMessageCode) !== -1;

        this.newMessageSubject.next(isNew);
      }
    });

    return this.newMessageSubject.asObservable();
  }

  public readMessages() {
    return new Promise<ServerResponse<any>>(res => {
      const request = new RequestOptions();
      request.Id_session = this.userCredentials.Id_Session;
      request.Id_app = this.Config.AppId;
      request.ApiVersion = 'v1';
      request.ManualJSON = 0;
      request.Controller0 = 'User';
      request.EntityId0 = this.userCredentials.Id_User;
      request.Method = 'Update';
      request.ReadMessage = 1;

      this.restService.post(request).then(readMessageResult => {
        if (readMessageResult !== null) {
          const response: ServerResponse<any> = readMessageResult;
          this.getNewMessageInfo().subscribe(newMessage => {
            res(response);
          });
        }
      });
    });
  }

  public isAuthenticated(forceUpdate: boolean = false): Promise<any> {
    const promise = new Promise<any>(resolve => {
      this.getCredentialsPromise(forceUpdate, true).then(cred => {
        const isAnonymous = (cred && cred.Id_User === 2);
        const isLoggedIn = (cred && cred.Id_Session !== undefined);
        const result = {
          'isAnonymous': isAnonymous,
          'isLoggedIn': isLoggedIn,
          'SsoAuth': cred.SsoAuth
        };
        resolve(result);
      });
    });
    return promise;
  }

  public isPremium(): Promise<boolean> {
    const promise = new Promise<boolean>(resolve => {
      this.getCredentialsPromise(false, true).then(cred => {
        resolve(cred && cred.Id_Tarif > 1);
      });
    });
    return promise;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/account.service.ts